import React, { useEffect, useState } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { createAuth, Session } from './auth/session';
import { webConfig, WebConfig } from './config';
import { withDeadline } from './network/deadline';
import RequestLoading from './components/RequestLoading';
import { Button } from './components/ui/button';
import SignIn from './components/SignIn';
import WorkspaceLoadingBoundary from './components/WorkspaceLoadingBoundary';
import './foundation.css';
import './shadcn.css';
import './form-consistency.css';
const SignedInWorkspace = React.lazy(() => withDeadline(() => import('./FarmWorkspace'), 45000));

/**
 * Restore sign-in before showing the workspace or sign-in form. A failed restore
 * without a session offers retry or manual sign-in; obsolete attempts cannot
 * update recovery errors or loading state.
 */
function Application({ config }: { config: WebConfig }) {
  const [auth] = useState(() => createAuth(config));
  const [session, setSession] = useState<Session | null>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState('');
  const [restoreFailed, setRestoreFailed] = useState(false);
  const [restoreAttempt, setRestoreAttempt] = useState(0);
  useEffect(() => {
    const stop = auth.subscribe(value => { setSession(value); if (value) setError(''); });
    let cancelled = false;
    setReady(false);
    void auth.restore().catch(() => {if (!cancelled) {setRestoreFailed(true);setError('We could not restore your sign-in. Check your connection and try again.');}}).finally(() => {if (!cancelled) setReady(true);});
    return () => { cancelled = true; stop(); };
  }, [auth, restoreAttempt]);
  if (!ready) return <main><RequestLoading branded message="Restoring sign-in…"/></main>;
  if (restoreFailed && error && !session) return <main className="signin shadcn-scope"><h1>Could not restore sign-in</h1><p role="alert">{error}</p><Button onClick={() => {setError('');setRestoreFailed(false);setRestoreAttempt(value => value + 1);}}>Retry sign-in recovery</Button><Button variant="outline" onClick={() => {setError('');setRestoreFailed(false);}}>Sign in again</Button></main>;
  return <BrowserRouter>{error && <p role="alert">{error}</p>}{session ?
    <WorkspaceLoadingBoundary><React.Suspense fallback={<main><RequestLoading branded message="Opening your farm…"/></main>}><SignedInWorkspace key={session.user.id} config={config} auth={auth} session={session} logout={() => void auth.logout().catch(e => setError(String(e)))} /></React.Suspense></WorkspaceLoadingBoundary> :
    <SignIn auth={auth} demo={config.authMode === 'demo'} />}</BrowserRouter>;
}
export default function FoundationApp() {
  const [result] = useState(() => { try { return { config: webConfig(), error: '' }; } catch (e) { return { error: String(e), config: undefined }; } });
  return result.config ? <Application config={result.config} /> : <main className="signin"><h1>Application setup required</h1><p>{result.error}</p><p>For the local demo, follow the repository README.</p></main>;
}
